import React from "react";

const STATUS_TONES = {
  active: { color: "#10b981", ar: "نشط", en: "Active" },
  onleave: { color: "#f59e0b", ar: "في إجازة", en: "On Leave" },
  inactive: { color: "#ef4444", ar: "غير نشط", en: "Inactive" },
  suspended: { color: "#f97316", ar: "موقوف", en: "Suspended" },
};

/**
 * Employee card used in the side panel of the Changes-page modals (Employee
 * Action, Employee History): avatar, name, code, status, job details and the
 * current salary tiles (hidden in manager view).
 *
 * `s` is the createBulkStyles() object of the hosting modal.
 */
const EmployeeSummaryCard = ({ employee, name, t, s, language, hideSalary = false, style }) => {
  const k = s.k;
  const lang = language === "ar" ? "ar" : "en";
  const locale = lang === "ar" ? "ar-EG-u-nu-latn" : "en-GB";
  const emp = employee || {};
  const code = emp.clientCode || emp.infinityCode;

  const fmtDate = (v) => {
    if (!v) return null;
    const [y, m, d] = String(v).slice(0, 10).split("-").map(Number);
    if (!y || !m || !d) return String(v);
    return new Intl.DateTimeFormat(locale, { day: "2-digit", month: "short", year: "numeric" }).format(new Date(y, m - 1, d));
  };

  const initials = (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  const statusTone = emp.employeeState
    ? STATUS_TONES[String(emp.employeeState).replace(/[\s_-]/g, "").toLowerCase()]
    : null;

  const jobFacts = [
    [t.position, emp.position ?? emp.employeeType],
    [t.department, emp.department?.name ?? emp.employeeType],
    [t.directManager, emp.firstLineManagerName],
    [t.joinDate, fmtDate(emp.hiringDate)],
    [t.performanceRating, emp.performance],
  ].filter(([, v]) => v !== null && v !== undefined && v !== "");

  const salaryFacts = hideSalary
    ? []
    : [
        [lang === "ar" ? "الصافي" : "Net", emp.netSalary],
        [lang === "ar" ? "الإجمالي" : "Gross", emp.grossSalary],
        [lang === "ar" ? "التأميني" : "Insurance", emp.insuranceSalary],
      ].filter(([, v]) => v !== null && v !== undefined && v !== "");

  return (
    <div
      style={{
        padding: 12,
        borderRadius: 14,
        border: `1px solid ${k.border}`,
        background: k.dark ? "rgba(255,255,255,0.03)" : "#ffffff",
        ...style,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: "50%",
            background: k.accentSoft,
            color: k.accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 800,
            fontSize: 18,
            flexShrink: 0,
          }}
        >
          {initials}
        </div>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 16.5, lineHeight: 1.3, wordBreak: "break-word" }}>{name}</div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 5 }}>
            {code && <span style={{ fontSize: 13, color: k.muted, fontWeight: 600 }}>{t.code} · {code}</span>}
            {statusTone && <span style={s.pill(statusTone.color)}>{statusTone[lang]}</span>}
          </div>
        </div>
      </div>

      {jobFacts.length > 0 && (
        <dl style={{ margin: "10px 0 0", paddingTop: 10, borderTop: `1px solid ${k.border}`, display: "grid", gap: 6 }}>
          {jobFacts.map(([label, value]) => (
            <div key={label} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 14 }}>
              <dt style={{ color: k.muted, fontWeight: 500, flexShrink: 0 }}>{label}</dt>
              <dd style={{ margin: 0, fontWeight: 700, textAlign: "end", wordBreak: "break-word" }}>{value}</dd>
            </div>
          ))}
        </dl>
      )}

      {salaryFacts.length > 0 && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${k.border}` }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: k.muted, marginBottom: 6 }}>
            {t.currentSalaryTitle} ({t.egp})
          </div>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${salaryFacts.length}, minmax(0, 1fr))`, gap: 8 }}>
            {salaryFacts.map(([label, value]) => (
              <div key={label} style={{ padding: "6px 8px", borderRadius: 10, background: k.subtle2, textAlign: "center" }}>
                <div style={{ fontSize: 12, color: k.muted, fontWeight: 600 }}>{label}</div>
                <div style={{ fontSize: 14.5, fontWeight: 800, marginTop: 2 }}>
                  {Number(value).toLocaleString(locale, { maximumFractionDigits: 0 })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeSummaryCard;
